import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FilePdf, FileCsv, Code, Image as ImageIcon, DownloadSimple, ShareNetwork, ArrowsClockwise, Trash, Prohibit, NotePencil } from "@phosphor-icons/react";
import api, { apiErr, API } from "../api";
import { FounderShell, RANGES, useReauth } from "../components/founder";

const KINDS = [
  ["executive_pdf", "Executive PDF", FilePdf],
  ["full_pdf", "Full Founder PDF", FilePdf],
  ["mobile_png", "Mobile PNG 1080×1920", ImageIcon],
  ["json", "JSON Technical", Code],
];
const CSV_SETS = ["player_activity", "retention_cohorts", "gameplay_funnel", "expedition_analytics",
  "crew_analytics", "economy", "progression", "prestige", "daily_expeditions", "rival_engagement", "system_errors"];

export default function FounderReports() {
  const [range, setRange] = useState("last_30_days");
  const [tz] = useState("America/New_York");
  const [reports, setReports] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [ensureAuth, reauthModal] = useReauth();

  const loadReports = useCallback(() => api.get("/founder/reports").then((r) => setReports(r.data.reports)).catch(() => {}), []);
  const loadNotes = useCallback(() => api.get("/founder/notes").then((r) => setNotes(r.data.notes)).catch(() => {}), []);
  useEffect(() => { loadReports(); loadNotes(); }, [loadReports, loadNotes]);

  const authedDownload = async (opaqueId, filename, canShare) => {
    await ensureAuth();
    try {
      const res = await api.get(`/founder/reports/${opaqueId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Downloaded");
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Re-authentication required"); }
      else toast.error(apiErr(e));
    }
  };

  const generate = async (kind) => {
    setBusy(true);
    await ensureAuth();
    try {
      const { data } = await api.post("/founder/reports/generate", { kind, range, tz });
      toast.success("Report generated");
      loadReports();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const csvExport = async (dataset) => {
    await ensureAuth();
    try {
      const res = await api.get(`/founder/reports/csv/${dataset}?range=${range}&tz=${encodeURIComponent(tz)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `aurumward-${dataset.replace(/_/g, "-")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      loadReports();
      toast.success("CSV exported");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const revoke = async (id) => { try { await api.post(`/founder/reports/${id}/revoke`); toast.success("Revoked"); loadReports(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { try { await api.delete(`/founder/reports/${id}`); toast.success("Deleted"); loadReports(); } catch (e) { toast.error(apiErr(e)); } };
  const addNote = async () => { if (!noteBody.trim()) return; try { await api.post("/founder/notes", { body: noteBody, scope: "general" }); setNoteBody(""); loadNotes(); toast.success("Note saved"); } catch (e) { toast.error(apiErr(e)); } };
  const delNote = async (id) => { try { await api.delete(`/founder/notes/${id}`); loadNotes(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <FounderShell>
      <div className="space-y-4" data-testid="founder-reports-page">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Reports & Exports</h1>

        <div className="ff-panel rounded-md p-4">
          <div className="flex flex-wrap items-center gap-1.5" data-testid="report-range">
            {RANGES.map(([k, lbl]) => (
              <button key={k} onClick={() => setRange(k)} data-testid={`report-range-${k}`}
                className={`rounded-full border px-2.5 py-1 text-[11px] ${range === k ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted"}`}>{lbl}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="report-generators">
          {KINDS.map(([kind, label, Icon]) => (
            <button key={kind} disabled={busy} onClick={() => generate(kind)} data-testid={`gen-${kind}`}
              className="flex flex-col items-center gap-2 rounded-md border border-ff-brass/25 bg-ff-bg2/60 p-4 text-center transition-colors hover:border-ff-gold disabled:opacity-50">
              <Icon size={26} weight="duotone" className="text-ff-gold" />
              <span className="text-sm text-ff-parchment">{label}</span>
            </button>
          ))}
        </div>

        <div className="ff-panel rounded-md p-5" data-testid="csv-exports">
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-ff-gold"><FileCsv size={18} weight="duotone" /> CSV Aggregate Exports</h2>
          <p className="mb-3 text-xs text-ff-muted">Aggregates only — no emails, tokens or personal data. Raw event exports are disabled.</p>
          <div className="flex flex-wrap gap-2">
            {CSV_SETS.map((ds) => (
              <button key={ds} onClick={() => csvExport(ds)} data-testid={`csv-${ds}`}
                className="rounded-sm border border-ff-brass/25 px-2.5 py-1.5 text-xs text-ff-parchment transition-colors hover:border-ff-gold hover:text-ff-gold">{ds.replace(/_/g, " ")}</button>
            ))}
          </div>
        </div>

        <div className="ff-panel rounded-md p-5" data-testid="report-history">
          <h2 className="mb-3 text-lg font-semibold text-ff-gold">Report History</h2>
          {reports.length === 0 ? <p className="text-sm text-ff-muted">No reports yet.</p> : (
            <div className="space-y-2">
              {reports.map((r) => (
                <div key={r.opaque_id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-ff-brass/15 bg-ff-bg2/50 p-3" data-testid={`report-${r.opaque_id}`}>
                  <div>
                    <div className="text-sm text-ff-parchment">{r.filename}</div>
                    <div className="text-[11px] text-ff-muted">{r.kind} · {r.range_label} · {(r.size / 1024).toFixed(1)} KB · {r.revoked ? "REVOKED" : `expires ${String(r.expires_at).slice(0, 10)}`}</div>
                  </div>
                  <div className="flex gap-1.5">
                    {!r.revoked && r.kind !== "csv" && (
                      <button onClick={() => authedDownload(r.opaque_id, r.filename)} data-testid={`dl-${r.opaque_id}`} className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-2 py-1 text-xs text-ff-parchment hover:border-ff-gold hover:text-ff-gold"><DownloadSimple size={13} /> Download</button>
                    )}
                    <button disabled title="Share links disabled" className="flex items-center gap-1 rounded-sm border border-ff-brass/15 px-2 py-1 text-xs text-ff-muted opacity-50"><ShareNetwork size={13} /> Share</button>
                    <button onClick={() => revoke(r.opaque_id)} data-testid={`revoke-${r.opaque_id}`} className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-2 py-1 text-xs text-ff-muted hover:text-ff-parchment"><Prohibit size={13} /> Revoke</button>
                    <button onClick={() => del(r.opaque_id)} data-testid={`del-${r.opaque_id}`} className="flex items-center gap-1 rounded-sm border border-red-500/30 px-2 py-1 text-xs text-red-300 hover:border-red-500"><Trash size={13} /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ff-panel rounded-md p-5" data-testid="founder-notes">
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-ff-gold"><NotePencil size={18} weight="duotone" /> Private Founder Notes</h2>
          <p className="mb-2 text-xs text-ff-muted">Never visible to players, sponsors or ordinary admins.</p>
          <div className="flex gap-2">
            <input value={noteBody} onChange={(e) => setNoteBody(e.target.value)} data-testid="note-input" placeholder="Add a private note…" className="flex-1 rounded-sm border border-ff-brass/30 bg-ff-bg p-2 text-sm text-ff-parchment" />
            <button onClick={addNote} data-testid="note-add" className="rounded-sm bg-ff-gold px-3 py-1.5 text-sm font-semibold text-ff-bg hover:bg-ff-brass">Save</button>
          </div>
          <div className="mt-3 space-y-1.5">
            {notes.map((n) => (
              <div key={n.id} className="flex items-center justify-between border-b border-ff-brass/10 py-1.5 text-sm" data-testid={`note-${n.id}`}>
                <span className="text-ff-parchment">{n.body}</span>
                <button onClick={() => delNote(n.id)} className="text-ff-muted hover:text-red-300"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {reauthModal}
    </FounderShell>
  );
}
